import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchReports();
  }, [selectedDate]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reports/daily?date=${selectedDate}`);
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const chartData = reports.map(report => ({
    name: report.coach_name,
    replied: report.groups_replied,
    pending: report.groups_pending,
    rate: report.response_rate
  }));

  return (
    <div data-testid="reports-page" className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground tracking-tight">REPORTS</h1>
          <p className="text-muted-foreground mt-2 font-body">Daily performance metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <input
            data-testid="date-picker"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-input border border-border rounded-md px-3 py-2 text-foreground font-body text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <Card className="bg-card border border-border p-6 animate-pulse">
            <div className="h-64 bg-muted rounded"></div>
          </Card>
        </div>
      ) : reports.length === 0 ? (
        <Card className="bg-card border border-border p-12 text-center">
          <p className="text-muted-foreground">No reports found for this date. Generate a report to see metrics!</p>
        </Card>
      ) : (
        <>
          <Card className="bg-card border border-border p-6">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-6">Response Rate by Coach</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  dataKey="name" 
                  stroke="#a1a1aa"
                  style={{ fontFamily: 'Inter', fontSize: 12 }}
                />
                <YAxis 
                  stroke="#a1a1aa"
                  style={{ fontFamily: 'Inter', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#18181b', 
                    border: '1px solid #27272a',
                    borderRadius: '8px',
                    fontFamily: 'Inter'
                  }}
                />
                <Legend 
                  wrapperStyle={{ fontFamily: 'Inter', fontSize: 14 }}
                />
                <Bar dataKey="replied" fill="#3b82f6" name="Replied" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pending" fill="#eab308" name="Pending" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map((report) => (
              <Card key={report.coach_phone} data-testid={`report-card-${report.coach_phone}`} className="bg-card border border-border p-6 hover:border-primary/50 transition-colors duration-300">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-heading font-bold text-foreground">{report.coach_name}</h3>
                  <div className={`flex items-center gap-1 text-sm ${
                    report.response_rate >= 80 
                      ? 'text-green-500' 
                      : report.response_rate >= 50 
                      ? 'text-yellow-500' 
                      : 'text-red-500'
                  }`}>
                    {report.response_rate >= 80 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    <span className="font-bold">{report.response_rate}%</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Groups</span>
                    <span className="text-lg font-heading font-bold text-foreground">{report.total_groups}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Replied</span>
                    <span className="text-lg font-heading font-bold text-green-500">{report.groups_replied}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Pending</span>
                    <span className="text-lg font-heading font-bold text-yellow-500">{report.groups_pending}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${report.response_rate}%` }}
                    ></div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
